import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  InputAdornment,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  ContactMail as ContactMailIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { SendMessageDto, MessageDetail, Contact, UserSearchResult, SendMessageResponse, ContactSearchResponse } from '../../types';
import messageService from '../../services/messageService';
import contactService from '../../services/contactService';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { formatEmailAddress, extractUsername } from '../../services/systemConfigService';
import RichTextEditor from '../shared/RichTextEditor';
import { sanitizeHtmlForSubmission } from '@shared/richtext/officePaste';
import {
  getContactSearchValidationMessage,
  isContactSearchResponse,
  meetsContactSearchMinimum,
} from '../../utils/contactSearchRules';
import {
  deleteDraft as deleteLocalDraft,
  type LocalDraftMessage,
  upsertDraft,
} from '../../services/draftMessageStore';
import {
  applySendPostageResults,
  ensureContactLookupCached,
  fetchOutboundPostageFromApi,
  getCachedContactLookup,
  getCachedNickname,
  getCachedOutboundPostage,
  seedContactIdentityCache,
} from '../../services/contactIdentityCache';
import {
  getLocalLookupMap,
  readContactLocalStore,
  resolveOutboundPostageEstimate,
} from '../../services/contactLocalStore';
import { extractAlias, formatComposeRecipientAlias, formatComposeRecipientOptionLabel, formatComposeRecipientPrimary } from '../../utils/qixitAddress';

export type ComposeMode = 'reply' | 'replyAll' | 'forward';
export interface InitialComposeRecipient {
  address: string;
  displayName: string;
  maxPay: number;
  deliveryFeeRequired: number;
  type?: 'to' | 'cc' | 'bcc';
}

interface MessageComposerProps {
  onClose: () => void;
  onSent?: () => void;
  onDraftSaved?: () => void;
  replyTo?: MessageDetail;
  composeMode?: ComposeMode;
  initialDraft?: LocalDraftMessage | null;
  initialRecipients?: InitialComposeRecipient[];
}

interface RecipientEntry {
  address: string;
  displayName: string;
  maxPay: number;
  deliveryFeeRequired: number;
  type: 'to' | 'cc' | 'bcc';
}

interface UploadedAttachment {
  tempId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
  onClose,
  onSent,
  onDraftSaved,
  replyTo,
  composeMode,
  initialDraft,
  initialRecipients,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Recipients state
  const [recipients, setRecipients] = useState<RecipientEntry[]>([]);
  const [recipientType, setRecipientType] = useState<'to' | 'cc' | 'bcc'>('to');
  
  // Message content
  const [subject, setSubject] = useState<string>(() => {
    if (initialDraft) return initialDraft.subject || '';
    if (!replyTo) return '';
    if (composeMode === 'forward') return `Fwd: ${replyTo.subject || ''}`;
    return `Re: ${replyTo.subject || ''}`;
  });
  const [body, setBody] = useState<string>(() => {
    if (initialDraft) return initialDraft.body || '';
    if (!replyTo) return '';
    const dateStr = replyTo.sentDate ? new Date(replyTo.sentDate).toLocaleString() : '';
    if (composeMode === 'forward') {
      const from = replyTo.fromName ? `${replyTo.fromName} &lt;${replyTo.fromAddress}&gt;` : replyTo.fromAddress;
      return `<br><br><div style="border-top:1px solid #ccc;padding-top:10px;margin-top:10px"><p style="color:#555;font-size:0.9em;margin:0 0 8px 0"><strong>---------- Forwarded message ----------</strong><br><strong>From:</strong> ${from}<br><strong>Date:</strong> ${dateStr}<br><strong>Subject:</strong> ${replyTo.subject || '(No Subject)'}</p>${replyTo.body || ''}</div>`;
    }
    // reply / replyAll: quote the original
    const from = replyTo.fromName ? `${replyTo.fromName} &lt;${replyTo.fromAddress}&gt;` : replyTo.fromAddress;
    return `<br><br><blockquote style="border-left:3px solid #ccc;padding-left:12px;margin:0 0 0 8px;color:#555"><p style="font-size:0.9em;margin:0 0 8px 0;color:#555">On ${dateStr}, ${from} wrote:</p>${replyTo.body || ''}</blockquote>`;
  });
  
  // Payment options (defaults loaded from user settings per legacy clsMSRequestGetAccountInfo)
  const [transactionAmount, setTransactionAmount] = useState<string>('0');
  const [payFees, setPayFees] = useState<boolean>(initialDraft ? !!initialDraft.payFees : false);
  const [expirationDays, setExpirationDays] = useState<string>(initialDraft ? String(initialDraft.expirationDays ?? 90) : '90');
  
  // UI state
  const [showCc, setShowCc] = useState<boolean>(false);
  const [showBcc, setShowBcc] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pasteTruncatedWarning, setPasteTruncatedWarning] = useState<boolean>(false);
  const [postageAlertOpen, setPostageAlertOpen] = useState<boolean>(false);
  const [pendingRecipient, setPendingRecipient] = useState<RecipientEntry | null>(null);

  const BODY_SIZE_WARN_BYTES = 10 * 1024 * 1024; // 10 MB
  const bodySizeBytes = new Blob([body]).size;
  const bodyTooLarge = bodySizeBytes > BODY_SIZE_WARN_BYTES;
  
  // Address Book contacts
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactLookupByAddress, setContactLookupByAddress] = useState<Record<string, UserSearchResult>>({});
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  
  // User search dialog
  const [searchDialogOpen, setSearchDialogOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState<string>('');
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  
  // User delivery settings (defaults from legacy clsMSRequestGetAccountInfo)
  const [defaultMaxPay, setDefaultMaxPay] = useState<number>(0);
  const [_defaultExpirationDays, setDefaultExpirationDays] = useState<number>(90);
  
  // Attachments state
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draftId, setDraftId] = useState<string | null>(initialDraft?.id ?? null);

  const buildMessageDto = (): SendMessageDto => {
    const toRecipients = recipients.filter(r => r.type === 'to').map(r => r.address);
    const ccRecipients = recipients.filter(r => r.type === 'cc').map(r => r.address);
    const bccRecipients = recipients.filter(r => r.type === 'bcc').map(r => r.address);
    const amount = parseFloat(transactionAmount) || 0;
    const expiration = parseInt(expirationDays) || 90;
    const cleanBody = sanitizeHtmlForSubmission(body.trim());

    return {
      subject: subject.trim(),
      body: cleanBody,
      toRecipients,
      ccRecipients: ccRecipients.length > 0 ? ccRecipients : undefined,
      bccRecipients: bccRecipients.length > 0 ? bccRecipients : undefined,
      transactionAmount: amount,
      maxDeliveryFee: defaultMaxPay,
      payFees,
      expirationDays: expiration,
      attachmentTempIds: attachments.length > 0 ? attachments.map(a => a.tempId) : undefined,
      recipientPostageEstimates: recipients.map((r) => ({
        address: r.address,
        requiredPostage: r.deliveryFeeRequired,
      })),
    };
  };

  // Load contacts and user settings on mount
  useEffect(() => {
    loadContacts();
    loadUserSettings();

    if (initialDraft) {
      const draftRecipients: RecipientEntry[] = [
        ...(initialDraft.toRecipients ?? []).map((address) => ({
          address,
          displayName: address,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'to' as const,
        })),
        ...(initialDraft.ccRecipients ?? []).map((address) => ({
          address,
          displayName: address,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'cc' as const,
        })),
        ...(initialDraft.bccRecipients ?? []).map((address) => ({
          address,
          displayName: address,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'bcc' as const,
        })),
      ];
      setRecipients(draftRecipients);
      setShowCc((initialDraft.ccRecipients?.length ?? 0) > 0);
      setShowBcc((initialDraft.bccRecipients?.length ?? 0) > 0);
      setTransactionAmount((initialDraft.transactionAmount ?? 0).toString());
      setPayFees(!!initialDraft.payFees);
      setExpirationDays((initialDraft.expirationDays ?? 90).toString());
      return;
    }

    if (initialRecipients && initialRecipients.length > 0) {
      const seededRecipients: RecipientEntry[] = initialRecipients.map((recipient) => ({
        address: recipient.address,
        displayName: recipient.displayName || recipient.address,
        maxPay: Number.isFinite(recipient.maxPay) ? recipient.maxPay : 0,
        deliveryFeeRequired: Number.isFinite(recipient.deliveryFeeRequired) ? recipient.deliveryFeeRequired : 0,
        type: recipient.type ?? 'to',
      }));
      setRecipients(seededRecipients);
      setShowCc(seededRecipients.some((r) => r.type === 'cc'));
      setShowBcc(seededRecipients.some((r) => r.type === 'bcc'));
      return;
    }
    
    // Handle reply / replyAll / forward recipients
    if (replyTo) {
      if (composeMode === 'forward') {
        // Forward: no recipients pre-filled — user chooses where to send
        setRecipients([]);
      } else if (composeMode === 'replyAll') {
        // Reply All: original sender goes To; all other To+CC (excluding self) go CC
        const myAddress = user?.username?.toLowerCase();
        const ccAddresses = [
          ...(replyTo.toRecipients || []),
          ...(replyTo.ccRecipients || []),
        ].filter(addr => {
          const lower = addr.toLowerCase();
          return myAddress && !lower.includes(myAddress);
        });
        const ccEntries = ccAddresses.map(addr => ({
          address: addr,
          displayName: addr,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'cc' as const,
        }));
        setRecipients([
          {
            address: replyTo.fromAddress,
            displayName: replyTo.fromName || replyTo.fromAddress,
            maxPay: 0,
            deliveryFeeRequired: 0,
            type: 'to',
          },
          ...ccEntries,
        ]);
        if (ccEntries.length > 0) setShowCc(true);
      } else {
        // Reply: just the original sender
        setRecipients([{
          address: replyTo.fromAddress,
          displayName: replyTo.fromName || replyTo.fromAddress,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'to',
        }]);
      }
    }
  }, [replyTo, composeMode, initialDraft, initialRecipients, user?.username]);

  async function loadContacts() {
    if (!user?.username) {
      setContacts([]);
      setContactLookupByAddress({});
      setLoadingContacts(false);
      return;
    }

    const local = readContactLocalStore(user.username);
    if (local?.contacts?.length) {
      setContacts(local.contacts);
      setContactLookupByAddress(getLocalLookupMap(user.username));
      setLoadingContacts(false);
      return;
    }

    setLoadingContacts(true);
    try {
      const response = await api.get<Contact[]>('/api/contacts');
      const normalized = Array.isArray(response) ? response : [];
      setContacts(normalized);
      seedContactIdentityCache(user.username, normalized);
      setContactLookupByAddress(getLocalLookupMap(user.username));
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  }

  const loadUserSettings = async () => {
    try {
      const settings = await api.get<{
        defaultMaxPayAmount: number;
        defaultDaysUntilMailExpires: number;
      }>('/api/contacts/settings');
      
      // Apply user's default settings
      setDefaultMaxPay(settings.defaultMaxPayAmount || 0);
      
      // Set expiration days from user's default (legacy: intDefaultDaysMailExpires)
      const expDays = settings.defaultDaysUntilMailExpires || 90;
      setDefaultExpirationDays(expDays);
      setExpirationDays(expDays.toString());
    } catch (err) {
      console.error('Failed to load user settings:', err);
    }
  };

  const handleSearchUsers = useCallback(async () => {
    const term = searchTerm.trim();
    if (!meetsContactSearchMinimum(term)) {
      setSearchResults([]);
      setSearchMessage(getContactSearchValidationMessage(term));
      return;
    }

    setSearchLoading(true);
    setSubmittedSearchTerm(term);
    try {
      const data = await api.get<ContactSearchResponse>('/api/contacts/search', {
        params: { q: term },
      });
      if (isContactSearchResponse(data)) {
        setSearchMessage(data.status === 'ok' ? null : (data.message ?? null));
        setSearchResults(data.status === 'ok' ? data.results : []);
      } else {
        setSearchMessage(null);
        setSearchResults(Array.isArray(data) ? data as UserSearchResult[] : []);
      }
    } catch (err: any) {
      console.error('Search failed:', err);
      setSearchResults([]);
      setSearchMessage(err?.response?.status === 429
        ? 'Too many searches. Please wait a moment and try again.'
        : 'Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm]);

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSearchUsers();
    }
  };

  // Add recipient from search result
  const resolveContactForUsername = useCallback((username: string): Contact | undefined => {
    const key = username.trim().toLowerCase();
    return contacts.find((c) => extractAlias(c.address).toLowerCase() === key);
  }, [contacts]);

  const getContactLookup = useCallback((addressOrUsername: string): UserSearchResult | undefined => {
    if (!user?.username) return undefined;
    const key = extractAlias(addressOrUsername).toLowerCase();
    return getCachedContactLookup(user.username, addressOrUsername)
      ?? contactLookupByAddress[key];
  }, [contactLookupByAddress, user?.username]);

  const buildComposeRecipientLabelOptions = useCallback((
    contact: Contact,
    lookup?: UserSearchResult | null
  ) => {
    const resolvedLookup = lookup ?? getContactLookup(contact.address);
    return {
      nickname: user?.username ? getCachedNickname(user.username, contact.address) : contact.nickname,
      displayName: contact.displayName,
      canonicalFullName: resolvedLookup?.fullName,
      fullName: resolvedLookup?.fullName,
      address: contact.address,
      username: extractAlias(contact.address),
    };
  }, [getContactLookup, user?.username]);

  const resolveRecipientDisplayName = useCallback((
    address: string,
    options?: { nickname?: string | null; displayName?: string | null; fullName?: string | null; username?: string | null }
  ) => {
    const lookup = options?.username ? getContactLookup(options.username) : getContactLookup(address);
    return formatComposeRecipientPrimary({
      nickname: options?.nickname,
      displayName: options?.displayName,
      fullName: options?.fullName,
      canonicalFullName: lookup?.fullName,
      address,
      username: options?.username,
    });
  }, [getContactLookup]);

  const resolveOutboundEstimate = useCallback((address: string): number => {
    if (!user?.username) return defaultMaxPay;
    return resolveOutboundPostageEstimate(user.username, address, defaultMaxPay);
  }, [user?.username, defaultMaxPay]);

  const handleAddSearchResult = async (searchUser: UserSearchResult) => {
    const address = formatEmailAddress(searchUser.username);
    const matchedContact = resolveContactForUsername(searchUser.username);
    
    if (recipients.some(r => r.address.toLowerCase() === address.toLowerCase())) {
      setError('This recipient has already been added');
      return;
    }

    let deliveryFeeRequired = resolveOutboundEstimate(address);
    if (user?.username && getCachedOutboundPostage(user.username, address) === undefined) {
      try {
        deliveryFeeRequired = await fetchOutboundPostageFromApi(user.username, searchUser.username);
      } catch {
        deliveryFeeRequired = defaultMaxPay;
      }
    }
    
    addRecipientWithPostage({
      address,
      displayName: resolveRecipientDisplayName(address, {
        nickname: user?.username ? getCachedNickname(user.username, address) : matchedContact?.nickname,
        displayName: matchedContact?.displayName,
        fullName: searchUser.fullName,
        username: searchUser.username,
      }),
      maxPay: defaultMaxPay,
      deliveryFeeRequired,
      type: recipientType
    });

    setSearchDialogOpen(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Add recipient from contacts
  const handleContactSelect = (contact: Contact | null) => {
    if (contact) {
      if (recipients.some(r => r.address.toLowerCase() === contact.address.toLowerCase())) {
        setError('This recipient has already been added');
        return;
      }
      
      addRecipientWithPostage({
        address: contact.address,
        displayName: resolveRecipientDisplayName(contact.address, {
          nickname: user?.username ? getCachedNickname(user.username, contact.address) : contact.nickname,
          displayName: contact.displayName,
          username: extractAlias(contact.address),
        }),
        maxPay: 0,
        deliveryFeeRequired: resolveOutboundEstimate(contact.address),
        type: recipientType
      });
    }
  };

  const commitRecipient = useCallback((entry: RecipientEntry) => {
    setRecipients((prev) => {
      if (prev.some((recipient) => recipient.address.toLowerCase() === entry.address.toLowerCase())) {
        return prev;
      }
      return [...prev, entry];
    });
  }, []);

  const addRecipientWithPostage = useCallback((entry: RecipientEntry) => {
    const requiredPostage = resolveOutboundEstimate(entry.address);
    const withPostage = { ...entry, deliveryFeeRequired: requiredPostage };

    if (requiredPostage > defaultMaxPay + 0.0001) {
      setPendingRecipient(withPostage);
      setPostageAlertOpen(true);
      return;
    }

    commitRecipient(withPostage);
  }, [commitRecipient, defaultMaxPay, resolveOutboundEstimate]);

  const handleConfirmAddRecipient = () => {
    if (pendingRecipient) {
      commitRecipient(pendingRecipient);
    }
    setPostageAlertOpen(false);
    setPendingRecipient(null);
  };

  // Remove recipient
  const handleRemoveRecipient = (address: string) => {
    setRecipients(prev => prev.filter(r => r.address !== address));
  };

  // Upload an image file and return its URL for inline embedding in the editor body
  const uploadInlineImage = useCallback(async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('files', file);
    try {
      const response = await api.post<UploadedAttachment[]>('/api/Attachment/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (Array.isArray(response) && response.length > 0) {
        const uploaded = response[0];
        // Build the URL to the attachment so TipTap can insert it as <img src="...">
        return `/api/Attachment/temp/${uploaded.tempId}`;
      }
      throw new Error('No file returned from server');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload image');
      throw err;
    }
  }, []);

  // Handle file selection for attachments
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingAttachment(true);
    setError(null);

    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }

      const response = await api.post<UploadedAttachment[]>('/api/Attachment/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (Array.isArray(response)) {
        setAttachments(prev => [...prev, ...response]);
      }
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.error || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove an uploaded attachment
  const handleRemoveAttachment = async (tempId: string) => {
    try {
      await api.delete(`/api/Attachment/temp/${encodeURIComponent(tempId)}`);
      setAttachments(prev => prev.filter(a => a.tempId !== tempId));
    } catch (err: any) {
      console.error('Failed to remove attachment:', err);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };


  const validateRecipients = (): boolean => {
    const toRecipients = recipients.filter(r => r.type === 'to');
    if (toRecipients.length === 0) {
      setError('Please add at least one recipient');
      return false;
    }

    return true;
  };

  const sendMessageDto = async (messageDto: SendMessageDto): Promise<SendMessageResponse> => {
    return await messageService.sendMessage(messageDto);
  };

  const handleSaveDraft = () => {
    if (!user?.username) {
      setError('Unable to save draft: not authenticated');
      return;
    }

    const payload = buildMessageDto();
    const hasAnyContent = !!(
      payload.subject?.trim() ||
      payload.body?.trim() ||
      payload.toRecipients.length ||
      payload.ccRecipients?.length ||
      payload.bccRecipients?.length
    );
    if (!hasAnyContent) {
      setError('Add at least a recipient, subject, or message body before saving draft');
      return;
    }

    const saved = upsertDraft(user.username, payload, draftId ?? undefined);
    setDraftId(saved.id);
    setSuccess('Draft saved');
    onDraftSaved?.();
  };

  const handleSend = async () => {
    setError(null);
    setSuccess(null);

    if (!validateRecipients()) {
      return;
    }

    if (!subject.trim()) {
      if (!window.confirm('Send message without a subject?')) {
        return;
      }
    }

    const amount = parseFloat(transactionAmount);
    if (isNaN(amount) || amount < 0) {
      setError('Invalid transaction amount');
      return;
    }

    const expiration = parseInt(expirationDays);
    if (isNaN(expiration) || expiration < 1 || expiration > 365) {
      setError('Expiration days must be between 1 and 365');
      return;
    }

    setSending(true);

    try {
      const messageDto: SendMessageDto = buildMessageDto();

      console.log('Sending message:', messageDto);
      console.log('Body length:', messageDto.body.length);
      console.log('Body preview:', messageDto.body.substring(0, 200));
      const response = await sendMessageDto(messageDto);
      if (!response.success) {
        setError(response.message || 'Failed to send message');
        return;
      }
      setSuccess(response.message || 'Message queued for delivery');

      if (user?.username) {
        applySendPostageResults(user.username, response.recipientPostage);
        void Promise.all(
          (response.recipientPostage ?? []).map(async (decision) => {
            const alias = extractAlias(decision.address);
            if (!alias) return;
            if (!getCachedContactLookup(user.username!, decision.address)) {
              await ensureContactLookupCached(user.username!, alias);
            }
          })
        );
      }

      if (draftId && user?.username) {
        deleteLocalDraft(user.username, draftId);
        setDraftId(null);
      }
      
      // Invalidate balance cache to trigger refresh
      queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
      
      // Close composer after 2 seconds
      setTimeout(() => {
        if (onSent) {
          onSent();
        }
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Send message error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to send message';
      setError(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const formatRecipientChipLabel = (r: RecipientEntry) => {
    const alias = extractAlias(r.address) || r.displayName;
    return `${alias} · $${r.deliveryFeeRequired.toFixed(2)}`;
  };

  const renderRecipientChip = (r: RecipientEntry, variant: 'filled' | 'outlined' = 'filled') => (
    <Tooltip key={r.address} title={`Required postage: $${r.deliveryFeeRequired.toFixed(2)}`}>
      <Chip
        label={formatRecipientChipLabel(r)}
        onDelete={() => handleRemoveRecipient(r.address)}
        size="small"
        color={r.deliveryFeeRequired > 0 ? 'info' : 'default'}
        variant={variant === 'outlined' ? 'outlined' : 'filled'}
      />
    </Tooltip>
  );

  const getRecipientsByType = (type: 'to' | 'cc' | 'bcc') => 
    recipients.filter(r => r.type === type);

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">
          {composeMode === 'forward' ? 'Forward Message' : composeMode === 'replyAll' ? 'Reply All' : replyTo ? 'Reply to Message' : 'Compose Message'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Form */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {/* Recipients Section */}
        <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContactMailIcon fontSize="small" />
            Recipients
          </Typography>
          
          {/* Add from contacts or search */}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Autocomplete
              options={contacts}
              getOptionLabel={(option) => formatComposeRecipientOptionLabel(buildComposeRecipientLabelOptions(option))}
              loading={loadingContacts}
              onChange={(_, value) => handleContactSelect(value)}
              sx={{ flexGrow: 1 }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select from Address Book..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <ContactMailIcon fontSize="small" color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const labelOptions = buildComposeRecipientLabelOptions(option);
                const alias = formatComposeRecipientAlias(option.address);
                const primary = formatComposeRecipientPrimary(labelOptions);
                const outboundEstimate = resolveOutboundEstimate(option.address);
                return (
                <li {...props} key={option.contactID}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" fontWeight={600}>{primary}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">{alias}</Typography>
                      {outboundEstimate > 0 && (
                        <Chip 
                          label={`Requires $${outboundEstimate.toFixed(2)}`} 
                          size="small" 
                          color="info"
                          sx={{ height: 16, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                  </Box>
                </li>
              );}}
            />
            
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setSearchDialogOpen(true)}
              size="small"
            >
              Find User
            </Button>
          </Stack>
          
          {/* Recipient type toggle */}
          <Stack direction="row" spacing={0.75} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              Add to:
            </Typography>
            <Chip 
              label="To" 
              color={recipientType === 'to' ? 'primary' : 'default'}
              onClick={() => setRecipientType('to')}
              size="small"
            />
            <Chip 
              label="CC" 
              color={recipientType === 'cc' ? 'primary' : 'default'}
              onClick={() => { setRecipientType('cc'); setShowCc(true); }}
              size="small"
            />
            <Chip 
              label="BCC" 
              color={recipientType === 'bcc' ? 'primary' : 'default'}
              onClick={() => { setRecipientType('bcc'); setShowBcc(true); }}
              size="small"
            />
          </Stack>

          {/* To Recipients */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 34, lineHeight: '24px', fontWeight: 600 }}
            >
              To:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minHeight: 24 }}>
              {getRecipientsByType('to').map(r => renderRecipientChip(r))}
              {getRecipientsByType('to').length === 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ lineHeight: '24px' }}>
                  No recipients added
                </Typography>
              )}
            </Box>
          </Box>

          {/* CC Recipients */}
          {(showCc || getRecipientsByType('cc').length > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 34, lineHeight: '24px', fontWeight: 600 }}
              >
                CC:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minHeight: 24 }}>
                {getRecipientsByType('cc').map(r => renderRecipientChip(r, 'outlined'))}
                {getRecipientsByType('cc').length === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ lineHeight: '24px' }}>
                    None
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* BCC Recipients */}
          {(showBcc || getRecipientsByType('bcc').length > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 34, lineHeight: '24px', fontWeight: 600 }}
              >
                BCC:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minHeight: 24 }}>
                {getRecipientsByType('bcc').map(r => renderRecipientChip(r, 'outlined'))}
                {getRecipientsByType('bcc').length === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ lineHeight: '24px' }}>
                    None
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Subject Field */}
        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={sending}
          sx={{ mb: 2 }}
        />

        {/* Body Field - Rich Text Editor */}
        <Box sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <RichTextEditor
            value={body}
            onChange={setBody}
            disabled={sending}
            placeholder="Type your message here..."
            minHeight={260}
            maxBodyHeight="min(52vh, 420px)"
            onPasteTruncated={() => setPasteTruncatedWarning(true)}
            uploadImageFile={uploadInlineImage}
          />
        </Box>

        {/* Paste truncation notice */}
        {pasteTruncatedWarning && (
          <Alert
            severity="warning"
            onClose={() => setPasteTruncatedWarning(false)}
            sx={{ mb: 1 }}
          >
            Pasted content was too large and has been trimmed (limit: 2 MB of raw HTML). You can paste the remaining sections separately to continue building your message.
          </Alert>
        )}

        {/* Body size advisory */}
        {bodyTooLarge && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Your message is getting large (~{Math.round(bodySizeBytes / (1024 * 1024))} MB). Consider moving images to attachments for faster delivery.
          </Alert>
        )}

        

        {/* Attachments Section */}
        <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon fontSize="small" />
            Attachments
          </Typography>

          {/* File input (hidden) */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
          />

          {/* Add attachment button */}
          <Button
            variant="outlined"
            startIcon={uploadingAttachment ? <CircularProgress size={16} /> : <AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploadingAttachment}
            size="small"
            sx={{ mb: 1 }}
          >
            {uploadingAttachment ? 'Uploading...' : 'Add Attachment'}
          </Button>

          {/* Attachment list */}
          {attachments.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {attachments.map((attachment) => (
                <Chip
                  key={attachment.tempId}
                  icon={<AttachFileIcon />}
                  label={`${attachment.fileName} (${formatFileSize(attachment.fileSize)})`}
                  onDelete={() => handleRemoveAttachment(attachment.tempId)}
                  deleteIcon={<DeleteIcon />}
                  sx={{ m: 0.5 }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {attachments.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No attachments. Max 5MB per file.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Payment & Delivery Options */}
        <Typography variant="subtitle2" gutterBottom>
          Payment & Delivery Options
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Tooltip title="Amount to send with this message (paid on delivery)">
            <TextField
              label="Attached Amount ($)"
              type="text"
              value={transactionAmount}
              onChange={(e) => {
                // Only allow numbers and decimal points
                const val = e.target.value.replace(/[^0-9.]/g, '');
                setTransactionAmount(val);
              }}
              onBlur={(e) => {
                // Format to 2 decimal places on blur
                const num = parseFloat(e.target.value) || 0;
                setTransactionAmount(num.toFixed(2));
              }}
              disabled={sending}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ width: '170px' }}
              helperText="Amount paid on delivery"
              size="small"
            />
          </Tooltip>

          <Tooltip title="Message will expire after this many days if not delivered">
            <TextField
              label="Expires After (days)"
              type="number"
              value={expirationDays}
              onChange={(e) => setExpirationDays(e.target.value)}
              disabled={sending}
              inputProps={{ min: 1, max: 365 }}
              sx={{ width: '140px' }}
              helperText="Message expiration"
              size="small"
            />
          </Tooltip>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={payFees}
              onChange={(e) => setPayFees(e.target.checked)}
              disabled={sending}
            />
          }
          label="Sender pays transaction fees"
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Message Queue:</strong> Your message will be queued for delivery. 
            You can cancel undelivered messages from your Sent folder.
            Messages expire after {expirationDays} day(s) if not delivered.
          </Typography>
        </Alert>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {recipients.length} recipient(s){attachments.length > 0 && `, ${attachments.length} attachment(s)`}
        </Typography>

        <Box display="flex" gap={1}>
          <Button onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveDraft}
            disabled={sending}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSend}
            disabled={sending || recipients.length === 0}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </Box>
      </Box>

      {/* Error/Success Messages - Below Send Button */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mx: 2, mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* User Search Dialog */}
      <Dialog open={searchDialogOpen} onClose={() => {
        setSearchDialogOpen(false);
        setSearchTerm('');
        setSubmittedSearchTerm('');
        setSearchResults([]);
        setSearchMessage(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon color="primary" />
          Find Qixit User
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search by username or name. Type at least 4 letters, then press Enter. For broad names, use Address Book Find Contact to narrow by location.
          </Typography>
          
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Username or name (min. 4 letters, press Enter)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: searchLoading ? <CircularProgress size={20} /> : null,
              }}
            />
            <Button variant="contained" onClick={() => void handleSearchUsers()} disabled={searchLoading}>
              Search
            </Button>
          </Box>

          {searchMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {searchMessage}
            </Alert>
          )}

          {searchResults.length > 0 && (
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {searchResults.map((searchUser) => {
                  const matchedContact = resolveContactForUsername(searchUser.username);
                  const alias = formatComposeRecipientAlias(undefined, searchUser.username);
                  const address = `${alias}@qixit.com`;
                  const outboundEstimate = resolveOutboundEstimate(address);
                  const primary = formatComposeRecipientPrimary({
                    nickname: user?.username ? getCachedNickname(user.username, address) : matchedContact?.nickname,
                    displayName: matchedContact?.displayName,
                    canonicalFullName: searchUser.fullName,
                    fullName: searchUser.fullName,
                    username: searchUser.username,
                  });
                  return (
                  <ListItemButton 
                    key={searchUser.username}
                    onClick={() => void handleAddSearchResult(searchUser)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">{primary}</Typography>
                          {!searchUser.isActive && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary">{alias}</Typography>
                          <Chip
                            label={`Requires $${outboundEstimate.toFixed(2)}`}
                            size="small"
                            color="info"
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                );})}
              </List>
            </Paper>
          )}

          {submittedSearchTerm && searchResults.length === 0 && !searchLoading && !searchMessage && (
            <Alert severity="info">
              No users found matching "{submittedSearchTerm}"
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={postageAlertOpen} onClose={() => { setPostageAlertOpen(false); setPendingRecipient(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Postage Alert</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {pendingRecipient && (
              <>
                <strong>{extractAlias(pendingRecipient.address)}</strong> requires{' '}
                <strong>${pendingRecipient.deliveryFeeRequired.toFixed(2)}</strong> postage, which is above your alert
                threshold of <strong>${defaultMaxPay.toFixed(2)}</strong>.
              </>
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPostageAlertOpen(false); setPendingRecipient(null); }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmAddRecipient}>
            Add Recipient
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MessageComposer;
